import { useQuery } from '@tanstack/react-query';
import type { StudentDetailDto } from '@motoschool/shared';
import {
  formatDuration,
  formatMoney,
  HOUR_ENTRY_REASON_LABELS,
  PAYMENT_METHOD_LABELS,
} from '@motoschool/shared';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { Card, EmptyState, PageLoader, SectionTitle } from '../components/ui';
import { formatDateTime } from '../lib/format';

/** Кабинет ученика: сколько часов осталось и куда они делись. */
export function BalancePage(): React.JSX.Element {
  const { user, timeZone } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['students', user?.id, 'detail'],
    queryFn: () => api.get<StudentDetailDto>(`/users/${user?.id}/detail`),
    enabled: Boolean(user?.id),
  });

  if (isLoading) return <PageLoader />;
  if (!data) return <EmptyState title="Данные недоступны" />;

  const balance = data.balanceMinutes;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Баланс</h1>
        <p className="text-sm text-muted">Оплаченные часы и история занятий</p>
      </div>

      <Card className={balance < 0 ? 'border-danger/30 bg-red-50' : ''}>
        <p className="text-sm text-muted">Осталось занятий</p>
        <p
          className={`mt-1 text-3xl font-semibold tabular-nums ${
            balance < 0 ? 'text-danger' : 'text-ink'
          }`}
        >
          {formatDuration(balance)}
        </p>
        {balance < 0 && (
          <p className="mt-2 text-sm text-danger">
            Занятия проведены в долг. Пополните баланс у инструктора.
          </p>
        )}
        <div className="mt-4 flex gap-4 text-sm text-muted">
          <span>Оплачено: {formatMoney(data.paidMinor, data.currency)}</span>
          <span>Проведено: {data.lessonsCompleted}</span>
        </div>
      </Card>

      {data.nextLessonAt && (
        <Card>
          <p className="text-sm text-muted">Ближайшее занятие</p>
          <p className="mt-1 text-lg font-semibold">{formatDateTime(data.nextLessonAt, timeZone)}</p>
        </Card>
      )}

      <section>
        <SectionTitle>Оплаты</SectionTitle>
        {data.payments.length === 0 ? (
          <EmptyState title="Оплат ещё не было" description="Оплату вносит инструктор после получения денег." />
        ) : (
          <div className="space-y-2">
            {data.payments.map((item) => (
              <Card key={item.id} className="flex items-center justify-between gap-3 p-3">
                <div>
                  <p className="font-medium tabular-nums">
                    {formatMoney(item.amountMinor, item.currency)}
                  </p>
                  <p className="text-xs text-muted">
                    {PAYMENT_METHOD_LABELS[item.method]} ·{' '}
                    {formatDateTime(item.paidAt ?? item.createdAt, timeZone)}
                  </p>
                </div>
                <span className="text-sm font-semibold text-success tabular-nums">
                  +{formatDuration(item.minutesAdded)}
                </span>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section>
        <SectionTitle>История часов</SectionTitle>
        {data.hourEntries.length === 0 ? (
          <EmptyState title="Пока пусто" />
        ) : (
          <Card className="divide-y divide-line p-0">
            {data.hourEntries.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{HOUR_ENTRY_REASON_LABELS[entry.reason]}</p>
                  <p className="text-xs text-muted">{formatDateTime(entry.createdAt, timeZone)}</p>
                </div>
                <span
                  className={`shrink-0 text-sm font-semibold tabular-nums ${
                    entry.deltaMinutes < 0 ? 'text-danger' : 'text-success'
                  }`}
                >
                  {entry.deltaMinutes > 0 ? '+' : ''}
                  {formatDuration(entry.deltaMinutes)}
                </span>
              </div>
            ))}
          </Card>
        )}
      </section>
    </div>
  );
}
